<script setup lang="ts">
import { type ComponentPublicInstance, computed, onMounted, ref } from 'vue'

import cn from '@internal/tailwind-classmerge'
import { getDataURL } from '@shared/fondof-fn/image/thumbhash'
import { useIsScreenSmMax } from '@shared/fondof-fn/media-query/screen'
import CarouselItem from '@shared/fondof-ui/components/molecules/carousel/CarouselItem.vue'
import CarouselRoot from '@shared/fondof-ui/components/molecules/carousel/CarouselRoot.vue'
import CarouselScrollContainer from '@shared/fondof-ui/components/molecules/carousel/CarouselScrollContainer.vue'
import CarouselViewport from '@shared/fondof-ui/components/molecules/carousel/CarouselViewport.vue'
import type { EmblaCarouselProps } from '@shared/fondof-ui/components/molecules/carousel/types'
import CarouselPrevNextButton from '@storefront-satch/components/molecules/emblaCarousel/CarouselPrevNextButton.vue'
import CarouselScrollbar from '@storefront-satch/components/molecules/emblaCarousel/CarouselScrollbar.vue'
import type { EmblaCarouselType } from 'embla-carousel'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'

import { PlayIcon } from '@fond-of/satch-icons'

const props = withDefaults(
  defineProps<
    Pick<
      EmblaCarouselProps['options'],
      'axis' | 'slidesToScroll' | 'scrollbar'
    > & {
      media: {
        mediaType: string
        url: string
        thumbhash?: string | undefined
      }[]
      activeIndex: number
      thumbnailWidth?: string
      thumbnailHeight?: string
      thumbnailClass?: string
      thumbnailActiveClass?: string
      videoThumbnailImage?: string
      scrollContainerClass?: string
      spaceBetween?: string
    }
  >(),
  {
    axis: 'x',
    scrollbar: false,
    thumbnailWidth: undefined,
    thumbnailHeight: undefined,
    thumbnailClass: undefined,
    thumbnailActiveClass: undefined,
    videoThumbnailImage: undefined,
    scrollContainerClass: undefined,
    slidesToScroll: 1,
    spaceBetween: '0px',
  }
)

const emit = defineEmits<{
  startHover: [index: number]
  endHover: [void]
  clicked: [index: number]
  emblaApi: [api: EmblaCarouselType | undefined]
}>()

const imageLoadedStates = ref<boolean[]>([])
const imageRefs = ref<HTMLImageElement[]>([])

const initialStartIndex = ref<number>(props.activeIndex)

const isMobile = useIsScreenSmMax()

const setImgRef = (el: Element | ComponentPublicInstance | null) => {
  if (el as HTMLImageElement) imageRefs.value.push(el as HTMLImageElement)
}

const handleImageLoadAtIndex = (index: number) => {
  imageLoadedStates.value[index] = true
}

const imageSize = 120
const sizedUrl = (imageUrl: string, width = imageSize, height = imageSize) => {
  return imageUrl.replace(/(\.[a-zA-Z]+)(\?.*)?$/, `_${width}x${height}$1$2`)
}

const horizontalSpaceBetween = computed(() => {
  return props.axis === 'x' ? props.spaceBetween : '0px'
})

const verticalSpaceBetween = computed(() => {
  return props.axis === 'y' ? props.spaceBetween : '0px'
})

// native @load event can fire before Vue hydration completes
// checking img state onMounted, we make sure the loading state is updated
onMounted(() => {
  imageLoadedStates.value = props.media.map(() => false)

  imageRefs.value.forEach((img, index) => {
    if (img?.complete) {
      handleImageLoadAtIndex(index)
    }
  })
})
</script>

<template>
  <CarouselRoot
    :options="{
      slidesToScroll: props.slidesToScroll,
      dragFree: true,
      prevNextButtons: !isMobile,
      axis: props.axis,
      scrollbar: props.scrollbar,
      startIndex: initialStartIndex,
      containScroll: 'keepSnaps',
    }"
    :modules="[WheelGesturesPlugin()]"
    @mouseleave="emit('endHover')"
    @embla-api="emit('emblaApi', $event)"
  >
    <CarouselViewport>
      <CarouselScrollContainer :class="props.scrollContainerClass">
        <CarouselItem
          v-for="(item, index) in props.media"
          :key="index"
          class="size-auto"
          :style="`margin-right: ${horizontalSpaceBetween}; margin-bottom: ${verticalSpaceBetween};`"
        >
          <button
            :class="
              cn(
                'block',
                'relative size-full cursor-pointer overflow-hidden opacity-30',
                props.thumbnailClass,
                {
                  'md:hover:border-2 md:hover:border-gray-400 md:hover:opacity-100':
                    activeIndex !== index,
                  [thumbnailActiveClass]: activeIndex === index,
                  'flex aspect-square items-center justify-center':
                    item.mediaType === 'video',
                }
              )
            "
            :style="`width: calc(${props.thumbnailWidth}); height: ${props.thumbnailHeight};`"
            @mouseenter="emit('startHover', index)"
            @click="emit('clicked', index)"
          >
            <img
              v-if="item.mediaType !== 'video'"
              :ref="setImgRef"
              class="w-full align-top transition-opacity"
              :class="[{ 'opacity-0': !imageLoadedStates[index] }]"
              :src="sizedUrl(item.url)"
              data-chromatic="ignore"
              @load="handleImageLoadAtIndex(index)"
            />
            <img
              v-if="item.thumbhash"
              class="absolute inset-0 size-full scale-75 object-cover blur-md transition-opacity"
              :class="[{ 'opacity-0': imageLoadedStates[index] }]"
              :src="getDataURL(item.thumbhash)"
              aria-hidden="true"
            />

            <span
              v-if="item.mediaType === 'video'"
              class="border-primary flex size-[35px] items-center justify-center rounded-full border-2 sm:size-10"
            >
              <component
                :is="PlayIcon"
                class="text-primary size-6 sm:size-7"
              />
            </span>
          </button>
        </CarouselItem>
      </CarouselScrollContainer>

      <CarouselPrevNextButton
        type="prev"
        variant="filledWhiteBlue"
      />
      <CarouselPrevNextButton
        type="next"
        variant="filledWhiteBlue"
      />
    </CarouselViewport>

    <CarouselScrollbar class="px-4 pt-2" />
  </CarouselRoot>
</template>
